import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import { FileText, AlertTriangle, Star, CheckCircle, MessageSquareX, UserCheck, AlertCircle } from 'lucide-react';
import './HistoriqueNotes.css';

const getToken = () => localStorage.getItem('token');
const getApiUrl = (path) => {
  const base = import.meta.env.VITE_API_URL || '';
  const api = base.endsWith('/api') ? base : `${base}/api`;
  return `${api}${path}`;
};

const PRIORITE_CONFIG = {
  haute_importance: { label: 'Urgente', bg: '#fef2f2', color: '#dc2626', border: '#fecaca', order: 3 },
  importante:       { label: 'Importante', bg: '#fffbeb', color: '#d97706', border: '#fde68a', order: 2 },
  normale:          { label: 'Normale', bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', order: 1 },
};

const STATUT_CONFIG = {
  ouverte:   { label: 'Ouverte',   bg: '#f1f5f9', color: '#64748b' },
  en_cours:  { label: 'En cours',  bg: '#dbeafe', color: '#1d4ed8' },
  resolue:   { label: 'Résolue',   bg: '#dcfce7', color: '#166534' },
};

const PrioriteBadge = ({ priorite }) => {
  const cfg = PRIORITE_CONFIG[priorite] || PRIORITE_CONFIG.normale;
  return (
    <span className="hn-badge" style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
      {cfg.label}
    </span>
  );
};

const StatutBadge = ({ statut }) => {
  const cfg = STATUT_CONFIG[statut] || STATUT_CONFIG.ouverte;
  return (
    <span className="hn-badge" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function HistoriqueNotes({ onNavigate, onLogout, currentPage }) {
  // ── Data ──────────────────────────────────────────────────────────────────
  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, importantes: 0, hautePriorite: 0, enAttente: 0, resolues: 0, sansReponse: 0, affecteesAMoi: 0 });
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // ── Filters ───────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [filterPriorite, setFilterPriorite] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterDateDebut, setFilterDateDebut] = useState('');
  const [filterDateFin, setFilterDateFin] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  // ── UI ────────────────────────────────────────────────────────────────────
  const [expandedNote, setExpandedNote] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [reponseText, setReponseText] = useState({});
  const [addingReponse, setAddingReponse] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const searchTimeout = useRef(null);

  const currentUserId = localStorage.getItem('userId');

  // ── Fetch stats ──────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl('/clients/notes/stats'), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.data.success) setStats(res.data.data);
    } catch (e) { /* stats non-bloquants */ }
  }, []);

  // ── Fetch notes ──────────────────────────────────────────────────────────
  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, sort: sortBy };
      if (search.trim()) params.search = search.trim();
      if (filterPriorite) params.priorite = filterPriorite;
      if (filterStatut) params.statut = filterStatut;
      if (filterType) params.type = filterType;
      if (filterDateDebut) params.dateDebut = filterDateDebut;
      if (filterDateFin) params.dateFin = filterDateFin;

      // Onglets rapides
      if (activeTab === 'affectees') params.affecteA = currentUserId;
      if (activeTab === 'importantes') params.priorite = 'importante';
      if (activeTab === 'urgentes') params.priorite = 'haute_importance';
      if (activeTab === 'resolues') params.statut = 'resolue';
      if (activeTab === 'sans_reponse') {} // filtré côté client

      const res = await axios.get(getApiUrl('/clients/notes/all'), {
        params,
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.data.success) {
        let data = res.data.data || [];
        if (activeTab === 'sans_reponse') {
          data = data.filter(item => !item.note?.reponses || item.note.reponses.length === 0);
        }
        setNotes(data);
        setTotal(res.data.total || data.length);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (e) {
      setError('Erreur lors du chargement des notes');
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortBy, search, filterPriorite, filterStatut, filterType, filterDateDebut, filterDateFin, activeTab, currentUserId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // ── Auto-clear messages ───────────────────────────────────────────────────
  useEffect(() => {
    if (message || error) {
      const t = setTimeout(() => { setMessage(''); setError(''); }, 3500);
      return () => clearTimeout(t);
    }
  }, [message, error]);

  // ── Reset page on filter change ───────────────────────────────────────────
  useEffect(() => { setPage(1); }, [search, filterPriorite, filterStatut, filterType, filterDateDebut, filterDateFin, activeTab, sortBy]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimeout.current);
  };

  const handleUpdateNote = async (item) => {
    try {
      const { statut, priorite, affecteA } = editValues;
      const url = item.type === 'dossier'
        ? getApiUrl(`/clients/${item.clientId}/dossiers/${item.dossierId}/notes/${item.note._id}`)
        : getApiUrl(`/clients/${item.clientId}/notes/${item.note._id}`);
      await axios.put(url, { statut, priorite, affecteA: affecteA || undefined }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setEditingNote(null);
      setMessage('Note mise à jour ✓');
      await fetchNotes();
      await fetchStats();
    } catch (e) {
      setError('Erreur lors de la mise à jour');
    }
  };

  const handleAddReponse = async (item) => {
    const text = (reponseText[item.note._id] || '').trim();
    if (!text) return;
    try {
      const url = item.type === 'dossier'
        ? getApiUrl(`/clients/${item.clientId}/dossiers/${item.dossierId}/notes/${item.note._id}/reponses`)
        : getApiUrl(`/clients/${item.clientId}/notes/${item.note._id}/reponses`);
      await axios.post(url, { text }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setReponseText(prev => ({ ...prev, [item.note._id]: '' }));
      setAddingReponse(null);
      setMessage('Réponse ajoutée ✓');
      await fetchNotes();
      await fetchStats();
    } catch (e) {
      setError('Erreur lors de l\'ajout de la réponse');
    }
  };

  const handleNavigateToDossier = (item) => {
    if (item.dossierId && item.clientId) {
      onNavigate('detail-dossier', { clientId: item.clientId, dossierId: item.dossierId });
    } else if (item.clientId) {
      onNavigate('client-detail', { clientId: item.clientId });
    }
  };

  const resetFilters = () => {
    setSearch('');
    setFilterPriorite('');
    setFilterStatut('');
    setFilterType('');
    setFilterDateDebut('');
    setFilterDateFin('');
    setActiveTab('all');
    setSortBy('date_desc');
    setPage(1);
  };

  const TABS = [
    { key: 'all',          label: 'Toutes',          count: stats.total },
    { key: 'urgentes',     label: 'Urgentes',        count: stats.hautePriorite },
    { key: 'importantes',  label: 'Importantes',     count: stats.importantes },
    { key: 'affectees',    label: 'Mes notes',       count: stats.affecteesAMoi },
    { key: 'sans_reponse', label: 'Sans réponse',    count: stats.sansReponse },
    { key: 'resolues',     label: 'Résolues',        count: stats.resolues },
  ];

  return (
    <div className="hn-page">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

      <div className="hn-content backoffice-content">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="hn-header dashboard-header">
          <div>
            <h1 className="hn-title" style={{display: 'flex', alignItems: 'center', gap: '10px'}}><FileText size={28} color="#1e3a8a" /> Historique des Notes</h1>
            <p className="hn-subtitle">Gestion centralisée de toutes les notes clients & dossiers</p>
          </div>
          <div className="hn-header-actions">
            <button className="hn-btn-refresh" onClick={() => { fetchNotes(); fetchStats(); }} title="Actualiser">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: '8px' }}>
                <path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
              Actualiser
            </button>
          </div>
        </div>

        {/* ── Messages ─────────────────────────────────────────────────── */}
        {message && <div className="hn-alert hn-alert-success">{message}</div>}
        {error && <div className="hn-alert hn-alert-error">{error}</div>}

        {/* ── Stats Cards ──────────────────────────────────────────────── */}
        <div className="hn-stats-grid">
          <div className="hn-stat-card hn-stat-total">
            <div className="hn-stat-icon"><FileText size={24} color="#1e3a5f" /></div>
            <div className="hn-stat-info">
              <div className="hn-stat-value">{stats.total}</div>
              <div className="hn-stat-label">Total Notes</div>
            </div>
          </div>
          <div className="hn-stat-card hn-stat-urgent">
            <div className="hn-stat-icon"><AlertTriangle size={24} color="#dc2626" /></div>
            <div className="hn-stat-info">
              <div className="hn-stat-value">{stats.hautePriorite}</div>
              <div className="hn-stat-label">Urgentes</div>
            </div>
          </div>
          <div className="hn-stat-card hn-stat-importante">
            <div className="hn-stat-icon"><Star size={24} color="#d97706" /></div>
            <div className="hn-stat-info">
              <div className="hn-stat-value">{stats.importantes}</div>
              <div className="hn-stat-label">Importantes</div>
            </div>
          </div>
          <div className="hn-stat-card hn-stat-ouverte">
            <div className="hn-stat-icon"><AlertCircle size={24} color="#64748b" /></div>
            <div className="hn-stat-info">
              <div className="hn-stat-value">{stats.enAttente}</div>
              <div className="hn-stat-label">Ouvertes</div>
            </div>
          </div>
          <div className="hn-stat-card hn-stat-resolue">
            <div className="hn-stat-icon"><CheckCircle size={24} color="#16a34a" /></div>
            <div className="hn-stat-info">
              <div className="hn-stat-value">{stats.resolues}</div>
              <div className="hn-stat-label">Résolues</div>
            </div>
          </div>
          <div className="hn-stat-card hn-stat-sans-reponse">
            <div className="hn-stat-icon"><MessageSquareX size={24} color="#9333ea" /></div>
            <div className="hn-stat-info">
              <div className="hn-stat-value">{stats.sansReponse}</div>
              <div className="hn-stat-label">Sans réponse</div>
            </div>
          </div>
          <div className="hn-stat-card hn-stat-affectee">
            <div className="hn-stat-icon"><UserCheck size={24} color="#2563eb" /></div>
            <div className="hn-stat-info">
              <div className="hn-stat-value">{stats.affecteesAMoi}</div>
              <div className="hn-stat-label">Mes notes affectées</div>
            </div>
          </div>
        </div>

        {/* ── Onglets rapides ───────────────────────────────────────────── */}
        <div className="hn-tabs">
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={`hn-tab ${activeTab === tab.key ? 'hn-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              <span className="hn-tab-count">{tab.count}</span>
            </button>
          ))}
        </div>

        {/* ── Filtres ───────────────────────────────────────────────────── */}
        <div className="hn-filters">
          <div className="hn-search-wrapper">
            <svg className="hn-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              className="hn-search-input"
              placeholder="Rechercher dans les notes..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <select className="hn-select" value={filterPriorite} onChange={e => setFilterPriorite(e.target.value)}>
            <option value="">Toutes priorités</option>
            <option value="haute_importance">Urgente</option>
            <option value="importante">Importante</option>
            <option value="normale">Normale</option>
          </select>
          <select className="hn-select" value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
            <option value="">Tous statuts</option>
            <option value="ouverte">Ouverte</option>
            <option value="en_cours">En cours</option>
            <option value="resolue">Résolue</option>
          </select>
          <select className="hn-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">Tous types</option>
            <option value="dossier">Notes Dossier</option>
            <option value="client">Notes Client</option>
          </select>
          <select className="hn-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="date_desc">Plus récentes</option>
            <option value="date_asc">Plus anciennes</option>
            <option value="priorite_desc">Priorité ↓</option>
          </select>
          <input type="date" className="hn-select" value={filterDateDebut} onChange={e => setFilterDateDebut(e.target.value)} title="Date début" />
          <input type="date" className="hn-select" value={filterDateFin} onChange={e => setFilterDateFin(e.target.value)} title="Date fin" />
          <button className="hn-btn-reset" onClick={resetFilters}>↺ Réinitialiser</button>
        </div>

        {/* ── Résultats ─────────────────────────────────────────────────── */}
        <div className="hn-results-bar">
          <span className="hn-results-count">{total} note{total > 1 ? 's' : ''} trouvée{total > 1 ? 's' : ''}</span>
          <span className="hn-page-info">Page {page} / {totalPages}</span>
        </div>

        {/* ── Table des notes ───────────────────────────────────────────── */}
        {loading ? (
          <div className="hn-loading">
            <div className="hn-spinner"></div>
            <p>Chargement des notes...</p>
          </div>
        ) : notes.length === 0 ? (
          <div className="hn-empty">
            <div className="hn-empty-icon" style={{color: '#94a3b8', display: 'flex', justifyContent: 'center', marginBottom: '15px'}}><FileText size={48} strokeWidth={1.5} /></div>
            <h3>Aucune note trouvée</h3>
            <p>Essayez de modifier vos filtres ou créez de nouvelles notes depuis les dossiers.</p>
          </div>
        ) : (
          <div className="hn-notes-list">
            {notes.map((item, idx) => {
              const note = item.note;
              const isExpanded = expandedNote === note._id;
              const isEditing = editingNote === note._id;
              const isAddingReponse = addingReponse === note._id;
              const isUrgent = note.priorite === 'haute_importance';
              const isImportante = note.priorite === 'importante';

              return (
                <div
                  key={`${item.type}-${note._id}-${idx}`}
                  className={`hn-note-card ${isUrgent ? 'hn-note-urgent' : isImportante ? 'hn-note-importante' : ''}`}
                >
                  {/* ── En-tête de la note ──────────────────────────────── */}
                  <div className="hn-note-header">
                    <div className="hn-note-meta">
                      <div className="hn-note-badges">
                        <PrioriteBadge priorite={note.priorite} />
                        <StatutBadge statut={note.statut} />
                        <span className={`hn-type-badge ${item.type === 'dossier' ? 'hn-type-dossier' : 'hn-type-client'}`}>
                          {item.type === 'dossier' ? '📁 Dossier' : '👤 Client'}
                        </span>
                      </div>
                      <div className="hn-note-author">
                        <strong>{note.author || 'Inconnu'}</strong>
                        <span className="hn-note-date">{formatDate(note.date)}</span>
                      </div>
                    </div>
                    <div className="hn-note-actions-top">
                      <button
                        className="hn-btn-icon hn-btn-edit"
                        title="Modifier statut/priorité"
                        onClick={() => {
                          setEditingNote(isEditing ? null : note._id);
                          setEditValues({ statut: note.statut || 'ouverte', priorite: note.priorite || 'normale', affecteA: '' });
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button
                        className="hn-btn-icon hn-btn-navigate"
                        title={item.type === 'dossier' ? 'Voir le dossier' : 'Voir le client'}
                        onClick={() => handleNavigateToDossier(item)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      </button>
                    </div>
                  </div>

                  {/* ── Liens client / dossier ──────────────────────────── */}
                  <div className="hn-note-links">
                    <button className="hn-link-btn" onClick={() => onNavigate('client-detail', { clientId: item.clientId })}>
                      👤 {item.clientNom} {item.clientPrenom || ''} {item.clientCin ? `(${item.clientCin})` : ''}
                    </button>
                    {item.dossierRef && (
                      <button className="hn-link-btn hn-link-dossier" onClick={() => handleNavigateToDossier(item)}>
                        📁 Réf: {item.dossierRef} {item.dossierEtat ? `— ${item.dossierEtat}` : ''}
                      </button>
                    )}
                  </div>

                  {/* ── Contenu ─────────────────────────────────────────── */}
                  <div className="hn-note-text">{note.text}</div>

                  {/* ── Mode édition ────────────────────────────────────── */}
                  {isEditing && (
                    <div className="hn-edit-panel">
                      <div className="hn-edit-row">
                        <label>Priorité :</label>
                        <select value={editValues.priorite} onChange={e => setEditValues(prev => ({ ...prev, priorite: e.target.value }))} className="hn-select-sm">
                          <option value="normale">✓ Normale</option>
                          <option value="importante">⭐ Importante</option>
                          <option value="haute_importance">🔥 Urgente</option>
                        </select>
                        <label>Statut :</label>
                        <select value={editValues.statut} onChange={e => setEditValues(prev => ({ ...prev, statut: e.target.value }))} className="hn-select-sm">
                          <option value="ouverte">Ouverte</option>
                          <option value="en_cours">En cours</option>
                          <option value="resolue">Résolue</option>
                        </select>
                        <button className="hn-btn-save" onClick={() => handleUpdateNote(item)}>✓ Sauvegarder</button>
                        <button className="hn-btn-cancel" onClick={() => setEditingNote(null)}>✕</button>
                      </div>
                    </div>
                  )}

                  {/* ── Fil de réponses ─────────────────────────────────── */}
                  <div className="hn-note-footer">
                    <button
                      className="hn-btn-reponses"
                      onClick={() => setExpandedNote(isExpanded ? null : note._id)}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                      {note.reponses?.length || 0} réponse{(note.reponses?.length || 0) > 1 ? 's' : ''}
                      {isExpanded ? ' ▲' : ' ▼'}
                    </button>
                    <button
                      className="hn-btn-add-reponse"
                      onClick={() => setAddingReponse(isAddingReponse ? null : note._id)}
                    >
                      + Répondre
                    </button>
                  </div>

                  {/* ── Réponses expandables ────────────────────────────── */}
                  {isExpanded && note.reponses?.length > 0 && (
                    <div className="hn-reponses-list">
                      {note.reponses.map((r, ri) => (
                        <div key={ri} className="hn-reponse-item">
                          <div className="hn-reponse-meta">
                            <strong>{r.author || 'Admin'}</strong>
                            <span className="hn-note-date">{formatDate(r.date)}</span>
                          </div>
                          <p className="hn-reponse-text">{r.text}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── Ajouter une réponse ─────────────────────────────── */}
                  {isAddingReponse && (
                    <div className="hn-add-reponse">
                      <textarea
                        className="hn-reponse-input"
                        placeholder="Votre réponse..."
                        value={reponseText[note._id] || ''}
                        onChange={e => setReponseText(prev => ({ ...prev, [note._id]: e.target.value }))}
                        rows={2}
                      />
                      <div className="hn-reponse-actions">
                        <button className="hn-btn-save" onClick={() => handleAddReponse(item)}>Envoyer</button>
                        <button className="hn-btn-cancel" onClick={() => setAddingReponse(null)}>Annuler</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Pagination ───────────────────────────────────────────────── */}
        {totalPages > 1 && (
          <div className="hn-pagination">
            <button className="hn-page-btn" disabled={page <= 1} onClick={() => setPage(1)}>«</button>
            <button className="hn-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const start = Math.max(1, page - 2);
              const p = start + i;
              if (p > totalPages) return null;
              return (
                <button
                  key={p}
                  className={`hn-page-btn ${p === page ? 'hn-page-active' : ''}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              );
            })}
            <button className="hn-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            <button className="hn-page-btn" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</button>
          </div>
        )}
      </div>
    </div>
  );
}
